import { isValidCountryCode } from "./countries.js";
import { isRestrictedGtin, isValidGtin } from "./gtin.js";
import type { Product } from "../product.js";
import type { ProductRule, RuleIssue } from "./types.js";

/**
 * Stammdatenregeln. Jede Regel hat eine stabile ID (für Overrides, Statistiken, UI-Filter).
 * Neue Regeln: hier ergänzen und RULESET_VERSION erhöhen → alle Artikel werden neu geprüft.
 */
export const RULESET_VERSION = "2026.10.5";

/**
 * Gesperrt sind nur Zeichen, die tatsächlich Probleme machen: Steuerzeichen (Tabulator, Zeilenumbruch) und unsichtbare
 * Leerzeichen (geschützt, schmal, Nullbreite – meist aus Copy & Paste). Normale Leerzeichen innerhalb der SKU
 * („P58 SO“) und Sonderzeichen („L-0768-1,5“) nimmt FFN an; am Anfang/Ende prüft die Regel davor (8.10.2026).
 */
// eslint-disable-next-line no-control-regex
const SKU_FORBIDDEN = /[\u0000-\u001f\u007f\u00a0\u2000-\u200b\u2028\u2029\u202f\u205f\u3000\ufeff]/;

const issue = (field: string, message: string, hint?: string): RuleIssue => ({ field, message, hint });

export const productRules: ProductRule[] = [
  // ── Identifikation ────────────────────────────────────────────────
  {
    id: "sku.required",
    category: "identification",
    defaultSeverity: "error",
    description: "SKU muss gesetzt sein",
    check: (p) => (p.sku.trim() ? [] : [issue("sku", "Die SKU fehlt.", "Vergib eine eindeutige Artikelnummer.")]),
  },
  {
    id: "sku.format",
    category: "identification",
    defaultSeverity: "error",
    description: "SKU ohne Steuer-/unsichtbare Zeichen",
    check: (p) => {
      if (!p.sku) return [];
      if (p.sku !== p.sku.trim()) return [issue("sku", "Die SKU enthält führende oder nachgestellte Leerzeichen.", "Leerzeichen entfernen.")];
      if (SKU_FORBIDDEN.test(p.sku))
        return [issue("sku", `Die SKU „${p.sku}" enthält unsichtbare oder Steuerzeichen (z. B. Tabulator, geschütztes Leerzeichen).`, "Die SKU neu eintippen statt kopieren.")];
      return [];
    },
  },
  {
    id: "sku.max_length",
    category: "identification",
    defaultSeverity: "error",
    description: "SKU-Länge innerhalb der Backend-Limits",
    check: (p, ctx) =>
      ctx.backends
        .filter((b) => p.sku.length > b.skuMaxLength)
        .map((b) => issue("sku", `Die SKU ist länger als ${b.skuMaxLength} Zeichen (Limit ${b.backend}).`)),
  },
  {
    id: "sku.unique",
    category: "identification",
    defaultSeverity: "error",
    description: "SKU je Mandant eindeutig",
    check: async (p, ctx) =>
      p.sku && (await ctx.lookups.countOtherProductsWithSku(p.sku, p.id)) > 0
        ? [issue("sku", `Die SKU „${p.sku}" wird bereits von einem anderen Artikel verwendet.`, "Doppelte Artikel zusammenführen oder SKU ändern.")]
        : [],
  },
  {
    id: "name.required",
    category: "content",
    defaultSeverity: "error",
    description: "Artikelbezeichnung gesetzt",
    check: (p) => (p.name.trim() ? [] : [issue("name", "Die Artikelbezeichnung fehlt.")]),
  },
  {
    id: "name.max_length",
    category: "content",
    defaultSeverity: "error",
    description: "Bezeichnung innerhalb der Backend-Limits",
    check: (p, ctx) =>
      ctx.backends
        .filter((b) => p.name.length > b.nameMaxLength)
        .map((b) => issue("name", `Die Bezeichnung ist länger als ${b.nameMaxLength} Zeichen (Limit ${b.backend}).`, "Bezeichnung kürzen.")),
  },
  {
    id: "gtin.recommended",
    category: "identification",
    defaultSeverity: "warning",
    description: "EAN/GTIN für Scan im Lager",
    check: (p, ctx) => {
      if (p.identifiers.gtin || p.bundle) return [];
      const required = ctx.backends.some((b) => b.requiresGtin);
      return [
        {
          ...issue("identifiers.gtin", "Keine EAN/GTIN hinterlegt – der Artikel kann im Lager nicht gescannt werden.", "EAN ergänzen oder Lager-Etikettierung beauftragen."),
          ...(required ? { severity: "error" as const } : {}),
        },
      ];
    },
  },
  {
    id: "gtin.checksum",
    category: "identification",
    defaultSeverity: "error",
    description: "Gültige GTIN-Prüfziffer",
    check: (p) => {
      const g = p.identifiers.gtin;
      if (!g) return [];
      return isValidGtin(g) ? [] : [issue("identifiers.gtin", `Die EAN „${g}" ist ungültig (Länge oder Prüfziffer falsch).`, "EAN von der Verpackung abgleichen – häufig ein Tippfehler oder fehlende führende Null.")];
    },
  },
  {
    id: "gtin.restricted_prefix",
    category: "identification",
    defaultSeverity: "info",
    description: "GTIN mit internem Präfix",
    check: (p) => {
      const g = p.identifiers.gtin;
      return g && isValidGtin(g) && isRestrictedGtin(g)
        ? [issue("identifiers.gtin", "Die EAN beginnt mit 2 – das ist eine interne bzw. selbst vergebene Nummer, keine GS1-registrierte EAN.")]
        : [];
    },
  },
  {
    id: "gtin.unique",
    category: "identification",
    defaultSeverity: "warning",
    description: "GTIN je Mandant eindeutig",
    check: async (p, ctx) => {
      const g = p.identifiers.gtin;
      return g && (await ctx.lookups.countOtherProductsWithGtin(g, p.id)) > 0
        ? [issue("identifiers.gtin", `Die EAN „${g}" ist mehreren Artikeln zugeordnet – Verwechslungsgefahr beim Kommissionieren.`)]
        : [];
    },
  },

  // ── Logistik ──────────────────────────────────────────────────────
  {
    id: "weight.required",
    category: "logistics",
    defaultSeverity: "error",
    description: "Bruttogewicht für Versandlabel",
    check: (p, ctx) => {
      if (p.weight?.grossKg && p.weight.grossKg > 0) return [];
      const severity = ctx.backends.some((b) => b.requiresWeight) ? "error" : "warning";
      return [{ ...issue("weight.grossKg", "Das Gewicht fehlt – ohne Gewicht können keine Versandlabels erzeugt werden.", "Artikel wiegen oder Gewicht aus dem Shop übernehmen."), severity }];
    },
  },
  {
    id: "weight.plausible",
    category: "logistics",
    defaultSeverity: "warning",
    description: "Gewicht plausibel",
    check: (p) => {
      const out: RuleIssue[] = [];
      const g = p.weight?.grossKg;
      const n = p.weight?.netKg;
      if (g !== undefined && g > 1000) out.push({ ...issue("weight.grossKg", `Gewicht ${g} kg ist unplausibel hoch.`, "Wurde Gramm statt Kilogramm eingetragen?"), severity: "error" });
      else if (g !== undefined && g > 31.5) out.push(issue("weight.grossKg", `Gewicht ${g} kg überschreitet die Paketgrenze von 31,5 kg – Versand nur als Sperrgut/Spedition.`));
      if (g !== undefined && n !== undefined && n > g) out.push({ ...issue("weight.netKg", "Das Nettogewicht ist größer als das Bruttogewicht."), severity: "error" });
      return out;
    },
  },
  {
    // Gewicht gegen Volumen: sehr leicht oder sehr schwer für die Größe deutet auf Tippfehler (g statt kg) oder Maße/Gewicht ohne Verpackung
    id: "weight.density",
    category: "logistics",
    defaultSeverity: "warning",
    description: "Gewicht passt zur Größe",
    check: (p, ctx) => {
      const g = p.weight?.grossKg;
      const d = p.dimensions;
      if (!g || !d || !(d.lengthCm > 0 && d.widthCm > 0 && d.heightCm > 0)) return [];
      // als korrekt bestätigt – gilt, solange Gewicht und Maße unverändert sind
      if (ctx.confirmed?.["weight.density"] && ctx.confirmed["weight.density"] === plausibilityFingerprint("weight.density", p)) return [];
      const liters = (d.lengthCm * d.widthCm * d.heightCm) / 1000;
      // sehr kleine Artikel (< 0,05 l, z. B. Ampullen) nicht bewerten – dort sind Maße oft gerundet
      if (liters < 0.05) return [];
      const density = g / liters;
      if (density >= 0.02 && density <= 3) return [];
      const fmt = (n: number) => n.toLocaleString("de-DE", { maximumFractionDigits: n < 1 ? 3 : 1 });
      return [issue("weight.grossKg", `Gewicht ${fmt(g)} kg wirkt ${density < 0.02 ? "sehr leicht" : "sehr schwer"} für die Größe (${fmt(liters)} l) – bitte Gewicht und Maße prüfen.`, "Gewicht inklusive Verpackung in kg und Maße in cm angeben.")];
    },
  },
  {
    id: "dimensions.required",
    category: "logistics",
    defaultSeverity: "warning",
    description: "Maße für Kartonwahl/Volumen",
    check: (p, ctx) => {
      const d = p.dimensions;
      if (d && d.lengthCm > 0 && d.widthCm > 0 && d.heightCm > 0) return [];
      const severity = ctx.backends.some((b) => b.requiresDimensions) ? "error" : "warning";
      return [{ ...issue("dimensions", "Maße (L × B × H) fehlen – Kartonwahl und Lagerplatzplanung sind dann nur geschätzt.", "Maße erfassen oder beim Wareneingang vermessen lassen."), severity }];
    },
  },
  {
    id: "dimensions.plausible",
    category: "logistics",
    defaultSeverity: "warning",
    description: "Maße und Dichte plausibel",
    check: (p) => {
      const d = p.dimensions;
      if (!d || !(d.lengthCm > 0 && d.widthCm > 0 && d.heightCm > 0)) return [];
      const out: RuleIssue[] = [];
      const sides = [d.lengthCm, d.widthCm, d.heightCm].sort((a, b) => b - a) as [number, number, number];
      if (sides[0] > 300) out.push({ ...issue("dimensions", `Längste Seite ${sides[0]} cm ist unplausibel.`, "Wurden Millimeter statt Zentimeter eingetragen?"), severity: "error" });
      else if (sides[0] + 2 * sides[1] + 2 * sides[2] > 300)
        out.push(issue("dimensions", "Gurtmaß über 300 cm – Versand nur als Sperrgut/Spedition."));
      const g = p.weight?.grossKg;
      if (g && g > 0) {
        const kgPerLitre = g / ((d.lengthCm * d.widthCm * d.heightCm) / 1000);
        if (kgPerLitre > 20) out.push(issue("dimensions", "Gewicht und Volumen passen nicht zusammen (dichter als Blei).", "Einheiten prüfen: kg/g bzw. cm/mm vertauscht?"));
        else if (kgPerLitre < 0.005) out.push(issue("dimensions", "Gewicht und Volumen passen nicht zusammen (extrem leicht für dieses Volumen).", "Einheiten prüfen: kg/g bzw. cm/mm vertauscht?"));
      }
      return out;
    },
  },

  // ── Zoll ──────────────────────────────────────────────────────────
  {
    id: "customs.hs_code",
    category: "customs",
    defaultSeverity: "error",
    description: "Zolltarifnummer für Drittlandversand",
    check: (p, ctx) => {
      const hs = p.customs.hsCode?.replace(/[\s.]/g, "");
      if (!hs) return ctx.settings.shipsOutsideEu ? [issue("customs.hsCode", "Zolltarifnummer fehlt – für Sendungen in Drittländer (z. B. CH, UK, NO) erforderlich.", "Nummer über tarif.zoll.de recherchieren.")] : [];
      return /^\d{6}(\d{2}(\d{2}(\d)?)?)?$/.test(hs) ? [] : [issue("customs.hsCode", `Die Zolltarifnummer „${p.customs.hsCode}" ist ungültig.`, "6, 8, 10 oder 11 Ziffern.")];
    },
  },
  {
    id: "customs.origin_country",
    category: "customs",
    defaultSeverity: "error",
    description: "Ursprungsland (ISO-2)",
    check: (p, ctx) => {
      const c = p.customs.countryOfOrigin;
      if (!c) return ctx.settings.shipsOutsideEu ? [issue("customs.countryOfOrigin", "Ursprungsland fehlt – für Drittlandversand erforderlich.")] : [];
      return isValidCountryCode(c) ? [] : [issue("customs.countryOfOrigin", `„${c}" ist kein gültiger ISO-Ländercode.`, "Zweistelliger Code, z. B. DE, CN.")];
    },
  },
  {
    id: "customs.value",
    category: "customs",
    defaultSeverity: "warning",
    description: "Warenwert für Zollinhaltserklärung",
    check: (p, ctx) =>
      ctx.settings.shipsOutsideEu && !p.customs.customsValue && !p.netRetailPrice
        ? [issue("customs.customsValue", "Kein Warenwert hinterlegt – wird für die Zollinhaltserklärung benötigt.")]
        : [],
  },

  // ── Handling ──────────────────────────────────────────────────────
  {
    id: "handling.dangerous_goods",
    category: "handling",
    defaultSeverity: "error",
    description: "Gefahrgut korrekt gekennzeichnet",
    check: (p) => {
      const dg = p.handling.dangerousGoods;
      if (!dg) return [];
      const un = (dg.unNumber ?? "").trim();
      if (!un || un.toUpperCase() === "UN")
        return [issue("handling.dangerousGoods.unNumber", "Als Gefahrgut markiert, aber die UN-Nummer fehlt.", "Unter Gefahrgut eintragen, z. B. UN3481 für Geräte mit Lithium-Akku – oder die Markierung entfernen.")];
      return /^UN\d{4}$/.test(un) ? [] : [issue("handling.dangerousGoods.unNumber", `UN-Nummer „${dg.unNumber}" ist ungültig.`, "Format: UN + 4 Ziffern, z. B. UN3481.")];
    },
  },
  {
    id: "price.non_negative",
    category: "content",
    defaultSeverity: "error",
    description: "Preise nicht negativ",
    check: (p) => {
      const out: RuleIssue[] = [];
      if (p.netRetailPrice && p.netRetailPrice.amountMinor < 0) out.push(issue("netRetailPrice", "Der Verkaufspreis ist negativ."));
      if (p.customs.customsValue && p.customs.customsValue.amountMinor < 0) out.push(issue("customs.customsValue", "Der Zollwert ist negativ."));
      return out;
    },
  },

  // ── Stücklisten / Sets ────────────────────────────────────────────
  {
    id: "bundle.components",
    category: "bundle",
    defaultSeverity: "error",
    description: "Stückliste vollständig und gültig",
    check: async (p, ctx) => {
      if (!p.bundle) return [];
      const comps = p.bundle.components;
      if (comps.length === 0) return [issue("bundle.components", "Das Set hat keine Komponenten.")];
      const out: RuleIssue[] = [];
      for (const [i, c] of comps.entries()) {
        if (c.productId === p.id) out.push(issue(`bundle.components.${i}`, "Ein Set darf sich nicht selbst enthalten."));
        if (!Number.isInteger(c.quantity) || c.quantity <= 0) out.push(issue(`bundle.components.${i}.quantity`, `Menge für „${c.sku}" muss eine positive ganze Zahl sein.`));
        if (!(await ctx.lookups.productExists(c.productId))) out.push(issue(`bundle.components.${i}`, `Komponente „${c.sku}" existiert nicht.`));
      }
      return out;
    },
  },
  // ── Gebinde ───────────────────────────────────────────────────────
  {
    id: "packUnits.valid",
    category: "identification",
    defaultSeverity: "error",
    description: "Gebinde mit Name, Menge > 1 und gültiger EAN",
    check: (p) => {
      const out: RuleIssue[] = [];
      for (const [i, u] of (p.packUnits ?? []).entries()) {
        if (!u.name.trim()) out.push(issue(`packUnits.${i}.name`, "Gebinde braucht einen Namen (z. B. „Karton 20“)."));
        if (!Number.isInteger(u.quantity) || u.quantity < 2) out.push(issue(`packUnits.${i}.quantity`, `Gebinde „${u.name}“: Menge muss eine ganze Zahl ab 2 sein.`));
        if (u.ean && !isValidGtin(u.ean)) out.push(issue(`packUnits.${i}.ean`, `Gebinde „${u.name}“: EAN ${u.ean} ist ungültig (Prüfziffer).`));
        if (u.ean && u.ean === p.identifiers.gtin) out.push(issue(`packUnits.${i}.ean`, `Gebinde „${u.name}“: EAN ist dieselbe wie die des Einzelartikels.`));
      }
      return out;
    },
  },
];

/** Plausibilitätshinweise, die der Kunde als korrekt bestätigen kann */
export const CONFIRMABLE_RULES = ["weight.density"] as const;
export type ConfirmableRule = (typeof CONFIRMABLE_RULES)[number];

/** Fingerabdruck der Werte, auf die sich eine Bestätigung bezieht – ändern sich die Werte, wird wieder geprüft */
export function plausibilityFingerprint(ruleId: string, p: Pick<Product, "weight" | "dimensions">): string {
  if (ruleId === "weight.density") return [p.weight?.grossKg ?? "", p.dimensions?.lengthCm ?? "", p.dimensions?.widthCm ?? "", p.dimensions?.heightCm ?? ""].join("|");
  return "";
}
